import {
  Box,
  Card,
  LinearProgress,
  Switch,
  Tooltip,
  Typography
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Section } from "components/disw";
import ErrorBox from "components/error/ErrorBox";
import DataGridView from "components/grid/DataGridView";
import { Column, Item, Toolbar } from "devextreme-react/data-grid";
import CustomStore from "devextreme/data/custom_store";
import {
  CostType,
  FilterOperator,
  QuantityUnit
} from "model/schema/globalTypes";
import { useMemo, useState } from "react";
import { useRateAgreementWithRates } from "./hooks/useRateAgreementWithRates";
import RateTableSummary from "./RateTableSummary";

function getCurrencySymbol(currency: string): string {
  try {
    const parts = new Intl.NumberFormat("en", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).formatToParts(0);
    return parts.find(p => p.type === "currency")?.value ?? currency;
  } catch {
    return currency;
  }
}

function filterOperatorSymbol(operator: FilterOperator): string {
  switch (operator) {
    case FilterOperator.EQ:
      return "==";
    case FilterOperator.LTE:
      return "\u2264";
    case FilterOperator.GTE:
      return "\u2265";
  }
}

function quantityUnitLabel(unit: QuantityUnit): string {
  switch (unit) {
    case QuantityUnit.KG:
      return "kg";
    case QuantityUnit.LB:
      return "lb";
    case QuantityUnit.CBM:
      return "m\u00B3";
    case QuantityUnit.CFT:
      return "ft\u00B3";
    case QuantityUnit.LDM:
      return "ldm";
  }
}

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    gap: theme.spacing(2)
  },
  structureCard: {
    padding: theme.spacing(2),
    backgroundColor: "#ffffff",
    border: "1px solid #00000026",
    borderRadius: 3,
    boxShadow: "0px 1px 2px 0px #0000004D",
    flexShrink: 0
  },
  propertyRow: {
    display: "flex",
    marginBottom: theme.spacing(0.5),
    alignItems: "flex-start"
  },
  property: {
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "16px",
    color: "#1E1E1E",
    minWidth: 112,
    maxWidth: 112,
    flexShrink: 0,
    paddingRight: theme.spacing(1)
  },
  value: {
    fontFamily: "Segoe UI",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px",
    color: "#464646"
  },
  gridRoot: {
    "&.dx-widget .dx-datagrid .dx-datagrid-header-panel": {
      backgroundColor: "#F5F7F7"
    },
    "&.dx-widget .dx-datagrid .dx-toolbar": { backgroundColor: "#F5F7F7" },
    "&.dx-widget .dx-datagrid .dx-datagrid-headers .dx-datagrid-table .dx-row > td":
      {
        backgroundColor: "#E6E6E6",
        fontWeight: 600,
        fontSize: 12,
        color: "#1E1E1E",
        padding: 0,
        paddingLeft: theme.spacing(1),
        height: 24,
        borderRight: "solid 1px #aaa",
        borderLeft: "solid 1px #aaa"
      },
    "&.dx-widget .dx-datagrid .dx-row-lines > td": {
      border: "none",
      padding: "4px 8px",
      height: 40,
      color: "#464646",
      fontSize: 12,
      fontWeight: 400
    },
    "&.dx-widget .dx-datagrid .dx-datagrid-rowsview .dx-row": {
      "&:hover": { backgroundColor: "#EEF6F8" }
    }
  },
  gridWrapper: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden"
  },
  rateCount: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#464646",
    fontFamily: "Segoe UI",
    marginRight: 16
  },
  viewToggle: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    paddingRight: 4
  },
  viewToggleLabel: {
    fontSize: "12px",
    fontFamily: "Segoe UI",
    color: "#464646",
    userSelect: "none"
  },
  viewToggleLabelActive: {
    fontWeight: 600,
    color: "#1E1E1E"
  },
  technicalBadge: {
    fontSize: "11px",
    fontFamily: "Segoe UI",
    color: "#666",
    background: "#EBEBEB",
    borderRadius: 4,
    padding: "2px 8px",
    userSelect: "none"
  }
}));

type ViewMode = "default" | "technical";

interface RateTableDetailsProps {
  agreementId: string;
  tableId: string;
}

interface RateRow {
  id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic columns built from rate structure
  [key: string]: any;
}

interface ViewModeLabelProps {
  mode: ViewMode;
  active: boolean;
  tooltip: string;
  onSelect: (mode: ViewMode) => void;
}

function ViewModeLabel({ mode, active, tooltip, onSelect }: ViewModeLabelProps) {
  const classes = useStyles();
  return (
    <Tooltip title={tooltip} placement="top" enterDelay={300} enterNextDelay={500} arrow>
      <Typography
        className={`${classes.viewToggleLabel}${active ? ` ${classes.viewToggleLabelActive}` : ""}`}
        onClick={() => onSelect(mode)}
        style={{ cursor: "pointer" }}
      >
        {mode === "default" ? "Default" : "Technical"}
      </Typography>
    </Tooltip>
  );
}

function buildFilterBand(
  filters: { column: string; displayName: string | null; operator: FilterOperator }[],
  prefix: string,
  caption: string,
  technical: boolean
) {
  if (filters.length === 0) return [];
  const children = filters.map((f, i) => ({
    colDef: {
      dataField: `${prefix}_${i}`,
      caption: `${technical || !f.displayName ? f.column : f.displayName} ${filterOperatorSymbol(f.operator)}`,
      allowSorting: true
    }
  }));
  return [{ colDef: { caption }, children }];
}

export default function RateTableDetails({
  agreementId,
  tableId
}: RateTableDetailsProps) {
  const classes = useStyles();
  const { rateAgreement, loading, error } =
    useRateAgreementWithRates(agreementId);

  const rateTable = useMemo(
    () => rateAgreement?.rateTables.find(rt => rt.id === tableId),
    [rateAgreement, tableId]
  );

  const hasAnyDisplayName = useMemo(() => {
    if (!rateTable) return false;
    const { rateStructure } = rateTable;
    return [
      ...rateStructure.pickupLocationFilters,
      ...rateStructure.deliveryLocationFilters,
      ...rateStructure.serviceOptionFilters
    ].some(f => !!f.displayName);
  }, [rateTable]);

  const [viewMode, setViewMode] = useState<ViewMode>("default");

  const [displayFormatter, fullFormatter] = useMemo(() => {
    try {
      return [
        new Intl.NumberFormat("en", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }),
        new Intl.NumberFormat("en", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 20
        })
      ];
    } catch {
      return [null, null];
    }
  }, []);

  const effectiveViewMode: ViewMode = !hasAnyDisplayName ? "technical" : viewMode;

  const filterColumns = useMemo(() => {
    if (!rateTable) return [];
    const { rateStructure } = rateTable;
    const technical = effectiveViewMode === "technical";
    return [
      ...buildFilterBand(rateStructure.pickupLocationFilters, "pickup", "Pickup Location", technical),
      ...buildFilterBand(rateStructure.deliveryLocationFilters, "delivery", "Delivery Location", technical),
      ...buildFilterBand(rateStructure.serviceOptionFilters, "option", "Service Options", technical)
    ];
  }, [rateTable, effectiveViewMode]);

  // Price columns — 3-level bands:
  //   Row 1: "{col} ({unit})" (band)
  //   Row 2: range bracket  e.g. "≤ 5000 kg" or "> 5000 kg" for open-ended
  //   Row 3: pricing unit   e.g. "$" for FIXED, "$/100 kg" for VARIABLE
  const priceColumnElements = useMemo(() => {
    if (!rateTable) return null;
    const { rateStructure } = rateTable;
    const pq = rateStructure.primaryQuantity;
    const unitLabel = quantityUnitLabel(pq.unit);
    const currencySymbol = getCurrencySymbol(rateTable.currency as string);

    const rangeCols = pq.ranges.map((r, i) => {
      const isFixed = r.costType === CostType.FIXED;
      const rangeCap =
        r.upperBound != null
          ? `\u2264 ${r.upperBound} ${unitLabel}`
          : `> ${pq.ranges[i - 1]?.upperBound ?? 0} ${unitLabel}`;
      const priceUnit = isFixed
        ? `${currencySymbol} flat`
        : `${currencySymbol}/${r.unitQuantity} ${unitLabel}`;
      const colWidth = Math.max(80, Math.max(rangeCap.length, priceUnit.length) * 7 + 24);

      return (
        <Column caption={rangeCap} key={i} alignment="right">
          <Column
            dataField={`charge_${i}`}
            caption={priceUnit.replace(" ", "\u00A0")}
            allowSorting={true}
            alignment="right"
            width={colWidth}
            cellRender={({ data }: { data: RateRow }) => {
              const raw = data[`charge_${i}`] as number | null | undefined;
              if (raw == null) return <span>{"\u2014"}</span>;
              const display = displayFormatter
                ? displayFormatter.format(raw)
                : (raw as number).toFixed(2);
              const full = fullFormatter ? fullFormatter.format(raw) : String(raw);
              return <span title={`${full} ${priceUnit}`}>{display}</span>;
            }}
          />
        </Column>
      );
    });

    return <Column caption={`${pq.column} (${unitLabel})`}>{rangeCols}</Column>;
  }, [rateTable, displayFormatter, fullFormatter]);

  const rows = useMemo<RateRow[]>(() => {
    if (!rateTable) return [];
    return rateTable.rates.map(rate => {
      const row: RateRow = { id: rate.id };
      rate.pickupLocations.forEach((v, i) => { row[`pickup_${i}`] = v; });
      rate.deliveryLocations.forEach((v, i) => { row[`delivery_${i}`] = v; });
      rate.serviceOptions.forEach((v, i) => { row[`option_${i}`] = v; });
      rate.charges.forEach((v, i) => { row[`charge_${i}`] = v; });
      return row;
    });
  }, [rateTable]);

  const dataSource = useMemo(
    () => new CustomStore({ key: "id", loadMode: "raw", load: () => rows }),
    [rows]
  );

  if (error) return <ErrorBox apolloError={error} />;
  if (!loading && !rateTable) return null;

  return (
    <div className={classes.root}>
      {loading && <LinearProgress />}
      <Card className={classes.structureCard}>
        <Section name="Rate Table Details">
          <div className={classes.propertyRow}>
            <Typography className={classes.property}>Description</Typography>
            <Typography
              className={classes.value}
              data-testid="rt-detail-description"
            >
              {rateTable?.description ?? "\u2014"}
            </Typography>
          </div>
          {rateTable && <RateTableSummary rateTable={rateTable} />}
        </Section>
      </Card>

      <div data-testid="rates-grid" className={classes.gridWrapper}>
        <DataGridView<RateRow>
          dataSource={dataSource}
          isLoading={loading}
          showFilterRow={true}
          allowSelection={false}
          height="100%"
          gridClasses={{ root: classes.gridRoot }}
          columns={filterColumns}
        >
          {priceColumnElements}
          <Toolbar>
            <Item
              location="after"
              render={() => (
                <Typography className={classes.rateCount}>
                  {rows.length} {rows.length === 1 ? "rate" : "rates"}
                </Typography>
              )}
            />
            <Item
              location="after"
              render={() => (
                <Box className={classes.viewToggle}>
                  {hasAnyDisplayName ? (
                    <>
                      <ViewModeLabel
                        mode="default"
                        active={effectiveViewMode === "default"}
                        tooltip="Show display names for filter columns (e.g. 'From Country' instead of 'ORIGIN_COUNTRY')"
                        onSelect={setViewMode}
                      />
                      <Switch
                        size="small"
                        checked={effectiveViewMode === "technical"}
                        onChange={() =>
                          setViewMode(m => m === "default" ? "technical" : "default")
                        }
                        inputProps={{ "aria-label": "view mode toggle" }}
                        data-testid="rt-view-toggle"
                      />
                      <ViewModeLabel
                        mode="technical"
                        active={effectiveViewMode === "technical"}
                        tooltip="Show technical property names as used in the system (e.g. 'ORIGIN_COUNTRY')"
                        onSelect={setViewMode}
                      />
                    </>
                  ) : (
                    <Tooltip
                      title="No display names were provided for this rate table. Column names shown are technical identifiers."
                      placement="top"
                      enterDelay={300}
                      arrow
                    >
                      <span className={classes.technicalBadge} data-testid="rt-technical-badge">
                        Technical view only
                      </span>
                    </Tooltip>
                  )}
                </Box>
              )}
            />
          </Toolbar>
        </DataGridView>
      </div>
    </div>
  );
}
